import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, User, Menu } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Logo from './Logo';
import MobileMenu from './MobileMenu';
import AnchorLink from './AnchorLink';

const links = [
  { label: 'Nouveautés', href: '#catalogue', id: 'new' },
  { label: 'Manifeste', href: '#manifeste', id: 'manifesto' },
  { label: 'Laboratoire', href: '#labo', id: 'lab' },
  { label: 'Blog', to: '/blog', id: 'blog' },
  { label: 'Contact', to: '/contact', id: 'contact' },
];

const Navbar = () => {
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();
  const prefix = pathname === '/' ? '' : '/';
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-0 top-0 z-50 glass-panel border-b border-white/5"
        data-testid="main-navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href={prefix === '' ? '#top' : '/'} data-testid="nav-logo" className="group">
            <Logo size={34} />
          </a>

          <nav className="hidden items-center gap-7 md:flex">
            <Link
              to="/boutique"
              data-testid="nav-link-boutique"
              className={`font-mono text-[11px] uppercase tracking-[0.25em] transition-colors hover:text-neon ${pathname === '/boutique' ? 'text-neon' : 'text-slate-400'}`}
            >
              Boutique
            </Link>
            {links.map((l) => (l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={`font-mono text-[11px] uppercase tracking-[0.25em] transition-colors hover:text-neon ${pathname.startsWith(l.to) ? 'text-neon' : 'text-slate-400'}`}
              >
                {l.label}
              </Link>
            ) : (
              <AnchorLink
                key={l.id}
                id={l.href.replace('#', '')}
                data-testid={`nav-link-${l.id}`}
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-neon"
              >
                {l.label}
              </AnchorLink>
            )))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/compte"
              data-testid="nav-link-account"
              aria-label="Mon compte"
              className={`hidden h-9 w-9 items-center justify-center border transition-all sm:flex ${pathname === '/compte' ? 'border-neon bg-neon/15 text-neon' : 'border-white/15 text-slate-400 hover:border-neon/50 hover:text-neon'}`}
            >
              <User size={15} />
            </Link>
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className="hidden font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-cyber lg:block"
            >
              Admin
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="cart-drawer-toggle"
              className="clip-frame-sm relative flex items-center gap-2 border border-neon/30 bg-neon/10 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon transition-all hover:bg-neon hover:text-void sm:px-4"
            >
              <ShoppingCart size={15} />
              <span className="hidden sm:inline">Panier</span>
              <span data-testid="cart-count-badge" className="flex h-5 min-w-5 items-center justify-center bg-neon px-1 text-[10px] font-bold text-void">
                {count}
              </span>
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Ouvrir le menu"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-neon/50 hover:text-neon md:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} links={links} />
    </>
  );
};

export default Navbar;
